import React, { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Clock, FileSpreadsheet, FileText, Loader2, Printer } from "lucide-react";
import { getTodayDate } from "../../utils/constants";
import useApi from "../../hooks/useApi";
import checkIfUserHasAccess from "../common/UserCheckAccess";
import logo from "../../utils/logo-cubix.png";
import { exportToCSV } from "../../hooks/useDocumentList";

const InvoiceSalesReportFilter = ({
  DEPARTMENT,
  COMPANY_CODE,
  BASE_URL,
  REPORT_HEADER_IMAGE,
  REPORT_FOOTER_IMAGE,
}) => {
  const [dateFrom, setDateFrom] = useState(getTodayDate);
  const [dateTo, setDateTo] = useState(getTodayDate);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [paymentType, setPaymentType] = useState("SALESREPORT_ALL");
  const [includeReturn, setIncludeReturn] = useState(false);
  const [salesmanList, setSalesmanList] = useState([]);
  const [selectedSalesman, setSelectedSalesman] = useState("-");
  const { request } = useApi();

  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "-";
  // console.log("CURRENT_USER_OBJECT", CURRENT_USER_OBJECT)
  const SALESMAN = CURRENT_USER_OBJECT[0]?.sales_man || "-";
  const salesmanParam = checkIfUserHasAccess("MP") ? selectedSalesman || "-" : SALESMAN;

  const getPaymentModes = () => {
    const baseModes = [
      { value: "ALL", label: "All" },
      { value: "CASH", label: "Cash" },
      { value: "CREDIT", label: "Credit" },
    ];

    return baseModes.map((mode) => ({
      value: includeReturn
        ? `SALESREPORT_${mode.value}_RET`
        : `SALESREPORT_${mode.value}`,
      label: mode.label,
    }));
  };

  const paymentModes = getPaymentModes();

  useEffect(() => {
    const fetchSalesmen = async () => {
      try {
        const url = `${BASE_URL}/api/MasterCount/${COMPANY_CODE}/SALESPERSONLIST/-/${DEPARTMENT}`;
        const response = await request({ url, method: "GET", timeout: 10000 });
        setSalesmanList(response || []);
      } catch {
        setSalesmanList([]);
      }
    };
    fetchSalesmen();
  }, []);

  useEffect(() => {
    if (!checkIfUserHasAccess("MP")) {
      setSelectedSalesman(SALESMAN);
    }
  }, []);

  const handleIncludeReturnChange = (checked) => {
    setIncludeReturn(checked);
    const baseType = paymentType
      .replace("SALESREPORT_", "")
      .replace("_RET", "");

    const newPaymentType = checked
      ? `SALESREPORT_${baseType}_RET`
      : `SALESREPORT_${baseType}`;
    setPaymentType(newPaymentType);
  };

  const fetchReports = async () => {
    const url = `${BASE_URL}/api/CRMSalesReport/${COMPANY_CODE}/${paymentType}/${dateFrom}/${dateTo}/${salesmanParam}/${DEPARTMENT}`;
    try {
      const response = await request({
        url: url,
        method: "GET",
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch Invoice details:", err);
      return [];
    }
  };

  const exportToPDFPreview = async () => {
    setLoading(true);

    const data = await fetchReports();
    if (!data || data.length === 0) {
      alert("No data available to generate the report.");
      setLoading(false);
      return;
    }

    setReportData(data);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 12;

    // --- Data Calculations ---
    const totalAmount = data.reduce((s, inv) => s + Number(inv["AMOUNT"] || 0), 0);
    const totalVAT = data.reduce((s, inv) => s + Number(inv["VATAMT"] || 0), 0);
    const netSales = totalAmount - totalVAT;

    // --- Table Layout ---
    const tableHead = [
      [
        "S.No.",
        "Dept No",
        "Inv No",
        "Inv Date",
        "Customer Details",
        "Salesman",
        "Amount",
      ],
    ];

    const tableBody = data.map((inv, i) => [
      i + 1,
      inv["DEPTNO"]?.trim() || "-",
      inv["INV NO"] || "-",
      inv["INV DATE"]?.split("T")[0] || "-",
      inv["CUSTOMER"]?.trim() || "-",
      inv["SALES MAN"] || inv["SALESMAN"] || "-",
      Number(inv["AMOUNT"] || 0).toFixed(2),
    ]);

    const getPaymentSubHeading = () => {
      const baseType = paymentType.replace("SALESREPORT_", "").replace("_RET", "");
      const typeText =
        baseType === "CASH"
          ? "Cash Sales Statement"
          : baseType === "CREDIT"
            ? "Credit Sales Statement"
            : "All Sales Statement";

      return includeReturn ? `${typeText} (Including Returns)` : typeText;
    };

    // --- Render Header Image ---
    const imgProps = doc.getImageProperties(REPORT_HEADER_IMAGE);
    const imgWidth = pageWidth;
    const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
    doc.addImage(REPORT_HEADER_IMAGE, "PNG", 0, 0, imgWidth, imgHeight);

    // --- Document Metadata & Title ---
    let currentY = imgHeight + 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(20, 24, 33);
    doc.text("INVOICE SUMMARY LIST", marginX, currentY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(100, 110, 125);
    doc.text(getPaymentSubHeading(), marginX, currentY + 5.5);

    // Right-aligned Generation Date
    const currentDateTime = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    doc.text(`Generated: ${currentDateTime}`, pageWidth - marginX, currentY, { align: "right" });
    //user name
    doc.setFontSize(9.5);
    doc.setTextColor(100, 110, 125);
    doc.text(`Username : ${CURRENT_USER_OBJECT[0]?.user}`, pageWidth - marginX + 10, currentY + 6, { align: "right" });
    currentY += 4;
    // Premium, Executive Divider Line
    currentY += 9;
    doc.setDrawColor(218, 224, 233);
    doc.setLineWidth(0.25);
    doc.line(marginX, currentY, pageWidth - marginX, currentY);

    // --- Executive Financial Summary Metrics Block ---
    currentY += 6;
    const totalAvailableWidth = pageWidth - (marginX * 2);
    const gap = 5;
    const cardWidth = (totalAvailableWidth - (gap * 2)) / 3;
    const cardHeight = 16;

    const summaryCards = [
      { label: "Net Revenue", val: netSales.toFixed(2), barColor: [71, 85, 105] },       // Slate Grey
      { label: "Value Added Tax", val: totalVAT.toFixed(2), barColor: [194, 120, 3] },   // Muted Ochre
      { label: "Gross Balance", val: totalAmount.toFixed(2), barColor: [30, 58, 138] },  // Navy
    ];

    summaryCards.forEach((card, idx) => {
      const cardX = marginX + idx * (cardWidth + gap);

      // 1. Subtle Structural Background Plate
      doc.setFillColor(248, 250, 253);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.rect(cardX, currentY, cardWidth, cardHeight, "FD");

      // 2. High-Precision Structural Vertical Accent Line
      doc.setDrawColor(card.barColor[0], card.barColor[1], card.barColor[2]);
      doc.setLineWidth(0.8); // Perfectly calibrated structural line thickness
      doc.line(cardX, currentY, cardX, currentY + cardHeight);

      // 3. Clean Metric Label Block
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(90, 95, 105);
      doc.text(card.label, cardX + 5, currentY + 5.2);

      // 4. Strong Corporate Numerical Reading
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12.5);
      doc.setTextColor(20, 24, 33);
      doc.text(card.val, cardX + 5, currentY + 11.8);
    });

    currentY += cardHeight + 7;

    // --- Table Execution ---
    autoTable(doc, {
      head: tableHead,
      body: tableBody,
      startY: currentY,
      theme: "grid",
      margin: { left: marginX, right: marginX, bottom: 35 },

      styles: {
        font: "helvetica",
        fontSize: 8.5,
        textColor: [40, 44, 55],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        cellPadding: 3,
        valign: "middle",
      },

      headStyles: {
        fillColor: [192, 192, 192], //  gray color
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineColor: [192, 192, 192],
        lineWidth: 0.2,
        halign: "left",
      },

      columnStyles: {
        0: { halign: "center", cellWidth: 12 },
        1: { halign: "center", cellWidth: 18 },
        2: { halign: "center", cellWidth: 22 },
        3: { halign: "center", cellWidth: 22 },
        4: { cellWidth: "auto" },
        5: { cellWidth: 28 },
        6: { halign: "right", fontStyle: "bold", cellWidth: 28 },
      },

      didParseCell: function (cellData) {
        if (cellData.section === "head" && [0, 1, 2, 3, 6].includes(cellData.column.index)) {
          cellData.cell.styles.halign = "center";
        }
        if (cellData.section === "body" && cellData.column.index === 6) {
          const amount = Number(cellData.cell.raw);
          if (amount < 0) {
            cellData.cell.styles.textColor = [190, 24, 24]; // Balanced Dark Crimson for returns
          }
        }
      },
    });

    // --- Dynamic Multi-Page Footer Setup ---
    const totalPages = doc.internal.getNumberOfPages();
    const footerImgHeight = 22;
    const footerImgY = pageHeight - footerImgHeight;

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      doc.addImage(
        REPORT_FOOTER_IMAGE,
        "PNG",
        0,
        footerImgY,
        pageWidth,
        footerImgHeight
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(120, 120, 120);
      const pageText = `Page ${i} of ${totalPages}`;
      doc.text(pageText, pageWidth - marginX, footerImgY - 3, { align: "right" });
    }

    const pdfBlob = doc.output("bloburl");
    window.open(pdfBlob);
    setLoading(false);
  };

  const exportToExcel = async () => {
    setLoading(true);
    const data = await fetchReports();
    if (!data || data.length === 0) {
      alert("No data available to export.");
      setLoading(false);
      return;
    }

    try {
      const columns = [
        { headerName: "S.No.", field: "sNo" },
        { headerName: "Dept No", field: "deptNo" },
        { headerName: "Inv No", field: "invNo" },
        { headerName: "Inv Date", field: "invDate" },
        { headerName: "Customer", field: "customer" },
        { headerName: "Salesman", field: "salesman" },
        { headerName: "Amount", field: "amount" },
        { headerName: "VAT Amt", field: "vatAmt" },
      ];

      const exportData = data.map((inv, i) => ({
        sNo: i + 1,
        deptNo: inv["DEPTNO"]?.trim() || "",
        invNo: inv["INV NO"] || "",
        invDate: inv["INV DATE"]?.split("T")[0] || "",
        customer: inv["CUSTOMER"] || "",
        salesman: inv["SALES MAN"] || inv["SALESMAN"] || "",
        amount: Number(inv["AMOUNT"] || 0).toFixed(2),
        vatAmt: Number(inv["VATAMT"] || 0).toFixed(2),
      }));

      const fileName = `Sales_Report_${dateFrom}_to_${dateTo}`;
      exportToCSV(columns, exportData, fileName);
    } catch (err) {
      console.error("Failed to export Excel/CSV file:", err);
      alert("An error occurred while exporting data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-orange-50/50 to-amber-50/30 border border-orange-200/60 rounded-xl overflow-hidden">
      <div className="bg-white/70 backdrop-blur-sm px-3 py-2 border-b border-orange-200/40 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-orange-100 flex items-center justify-center">
            <Clock className="w-3 h-3 text-orange-600" />
          </div>
          <span className="text-xs font-semibold text-slate-700 tracking-wider">
            SALES REPORT
          </span>
        </div>

        <div className="relative">
          <label className="group flex items-center gap-2 cursor-pointer bg-gradient-to-r from-white/80 to-white/60 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-orange-200/50 hover:border-orange-300 hover:from-white/90 hover:to-white/80 transition-all duration-200 shadow-sm hover:shadow-md">
            <div className="relative">
              <input
                type="checkbox"
                checked={includeReturn}
                onChange={(e) => handleIncludeReturnChange(e.target.checked)}
                className="sr-only"
              />
              <div
                className={`w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center ${includeReturn
                  ? "bg-gradient-to-br from-orange-500 to-orange-600 border-orange-500 shadow-sm"
                  : "bg-white border-gray-300 group-hover:border-orange-400"
                  }`}
              >
                {includeReturn && (
                  <svg
                    fill="none"
                    className="w-2.5 h-2.5 text-white"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </div>
            <span
              className={`text-xs font-medium transition-colors duration-200 ${includeReturn
                ? "text-orange-700"
                : "text-slate-600 group-hover:text-slate-700"
                }`}
            >
              Include Return
            </span>
          </label>
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="flex-1 px-2 py-1.5 text-sm border border-orange-200 bg-white/60 backdrop-blur-sm rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:bg-white transition-all"
          />
          <div className="w-3 h-0.5 bg-slate-300 rounded-full"></div>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="flex-1 px-2 py-1.5 text-sm border border-orange-200 bg-white/60 backdrop-blur-sm rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:bg-white transition-all"
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2">          <div className="flex items-center bg-white/60 backdrop-blur-sm rounded-lg p-1 gap-1 border border-gray-200">
          {paymentModes?.map((type) => (
            <label key={type.value} className="relative cursor-pointer">
              <input
                type="radio"
                name="payment"
                value={type.value}
                checked={paymentType === type.value}
                onChange={(e) => setPaymentType(e.target.value)}
                className="sr-only"
              />
              <div
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors duration-300 ease-linear ${paymentType === type.value
                  ? "bg-orange-500 text-white shadow-sm"
                  : "text-slate-600 hover:bg-orange-100"
                  }`}
              >
                {type.label}
              </div>
            </label>
          ))}
        </div>

          <select
            value={selectedSalesman}
            onChange={(e) => setSelectedSalesman(e.target.value)}
            disabled={!checkIfUserHasAccess("MP")}
            className="w-32 flex-1 px-2 py-1.5 text-sm border border-orange-200 bg-white/60 backdrop-blur-sm rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:bg-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {checkIfUserHasAccess("MP") && <option value="-">All Salesmen</option>}
            {salesmanList.map((s) => (
              <option key={s.EMP_ID} value={s.EMP_ID}>
                {s.NAME}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1.5">
            <button
              onClick={exportToExcel}
              disabled={loading}
              title="Export to Excel"
              className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200/60 transition-all shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            </button>
            <button
              onClick={exportToPDFPreview}
              disabled={loading}
              title="Export to PDF"
              className={`p-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200/60 transition-all shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSalesReportFilter;